import {
    PulseStorage,
    ResourceLock,
    SharedConfigurations,
    StorageChangeKeyMatch,
    StorageChangeOrigin,
    sleep,
} from '@pulse/common';
import { DateService } from '../date.service';
import { UUIDService } from '../uuid.service';
import { DocumentVisibilityTracker } from '../visibility.tracker';
import { LockManager } from './lock-manager';

/**
 * Provide simple cross-tab resource locking mechanism.
 * The lock will be held until it is explicitly released or
 * the document that holds it gets unloaded.
 */
export class UnsafeLockManager implements LockManager {
    private id: string;
    private locks: Map<string, ResourceLock>;

    constructor(
        private storage: PulseStorage, // PersistentStorage | TransientStorage
        private date: DateService,
        private config: SharedConfigurations,
        private uuid: UUIDService,
        visibilityTracker: DocumentVisibilityTracker,
    ) {
        this.id = uuid.generate();
        this.locks = this.syncLocks();

        this.storage
            .change('locks', StorageChangeKeyMatch.Full, StorageChangeOrigin.External)
            .subscribe((change) => {
                this.syncLocks(change.value);
            });

        visibilityTracker.legacyOnUnload().subscribe(() => this.releaseAll());
    }

    async release(lock: ResourceLock, sync = true): Promise<void> {
        const acquiredLock = this.locks.get(lock.name);

        if (!acquiredLock || acquiredLock.clientId !== this.id || acquiredLock.id !== lock.id) {
            return;
        }

        this.locks.delete(acquiredLock.name);

        if (sync) {
            this.syncLocks();
        }
    }

    async locked(key: string): Promise<boolean> {
        return this.locks.has(key);
    }

    async request(key: string, timeout = 100, backoff = 10): Promise<ResourceLock | null> {
        const start = this.date.now();

        while (true) {
            const lock = await this.lock(key);
            const timedOut = this.date.now() - start > timeout;

            if (lock || timedOut) {
                return lock;
            }

            await sleep(backoff);
        }
    }

    /**
     * Tries to lock a resource with a specific key
     * and returns true if the lock was successfully acquired.
     */
    private async lock(key: string): Promise<ResourceLock | null> {
        const locked = this.locks.has(key);

        if (locked) {
            return null;
        } else {
            const lock: ResourceLock = {
                name: key,
                clientId: this.id,
                id: this.uuid.generate(),
                createdDate: this.date.now(),
            };

            this.locks.set(key, lock);
            this.syncLocks();

            return lock;
        }
    }

    private syncLocks(override?: string | null): Map<string, ResourceLock> {
        if (!this.locks || override) {
            const storageLocksJSON = override || this.storage.get('locks');
            const storageLocksParsed = JSON.parse(storageLocksJSON || 'null');
            const storageLocks = new Map<string, ResourceLock>(storageLocksParsed || []);
            const freshLocks = this.releaseExpiredLocks(storageLocks);

            this.locks = freshLocks;

            if (storageLocks.size !== freshLocks.size) {
                this.writeLocks(freshLocks);
            }
        } else {
            this.writeLocks(this.locks);
        }

        return this.locks;
    }

    private writeLocks(locks: Map<string, ResourceLock>) {
        if (locks.size === 0) {
            this.storage.remove('locks');
        } else {
            this.storage.setJSON('locks', Array.from(locks));
        }
    }

    private releaseAll() {
        for (const [_name, lock] of this.locks) {
            void _name;

            if (lock.clientId === this.id) {
                this.release(lock, false);
            }
        }

        this.syncLocks();
    }

    private releaseExpiredLocks(locks: Map<string, ResourceLock>): Map<string, ResourceLock> {
        const now = this.date.now();
        const freshLocks = Array.from(locks).filter(([_key, lock]) => {
            void _key;

            return now - lock.createdDate < this.config.locksMaxAge;
        });

        return new Map(freshLocks);
    }
}
